import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import WavingHandEnvelope from '../../../assets/images/wavingHandEnvelope.svg';
import {isTrackerSubscriber} from '../../../helpers/modelHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  subscribeToConversationsAction,
  unsubscribeVisitorFromConversationsAction,
} from '../../../store/data/conversations/subscribeConversationsActions';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {TwoLinesDropdownItem} from '../../common/dropdowns/twoLinesDropdownItem';
import {PermissionPopoverCoordinator} from '../../common/popovers/permissionPopoverCoordinator';

/*
 * Props.
 */

interface VisitorConversationHeaderSubscribeProps {
  cellsContext: CellsContext;
  conversation: ConversationModel;
  isVisitorSubscribedAll: boolean;
  resubscribeAllVisitorNotifications: Handler;
}

/*
 * Style.
 */

const StyledSubscribeButtonDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 8px;
  align-items: center;
  padding-left: 3px;
`;

const StyledUnsubscribedAllDropdownDiv = styled.div`
  display: grid;
  grid-row-gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const StyledWavingHandEnvelopeImg = styled.img`
  margin: auto;
  width: 100px;
  height: 100px;
`;

const StyledUnsubscribedAllContentDiv = styled.div`
  margin-top: 8px;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

const StyledResubscribeButtontDiv = styled.div`
  text-transform: capitalize;
  padding: 0 58px;
`;

/*
 * Component.
 */

export const VisitorConversationsHeaderSubscribe: FC<VisitorConversationHeaderSubscribeProps> = (props) => {
  const dispatch = useAppDispatch();
  const {cellsContext, conversation, isVisitorSubscribedAll} = props;

  const isSubscribed =
    isVisitorSubscribedAll && isTrackerSubscriber(conversation.trackers[cellsContext.teammateId]);
  const onSubscribeClick = () => {
    dispatch(subscribeToConversationsAction(cellsContext, [conversation.id]));
  };
  const onUnsubscribeClick = () => {
    dispatch(unsubscribeVisitorFromConversationsAction(cellsContext, [conversation.id]));
  };

  return (
    <PermissionPopoverCoordinator
      renderButton={(isOpen) => renderButton(isOpen, isSubscribed)}
      renderDisabledButton={() => renderButton(false, isSubscribed)}
      renderModal={(modalProps) =>
        renderDropdown(modalProps, props, isSubscribed, onSubscribeClick, onUnsubscribeClick)
      }
    />
  );
};

/*
 * Button.
 */

function renderButton(isOpen: boolean, isSubscribed: boolean) {
  return (
    <Button hasHover={isOpen} isRounded interactionId="visitor-conversation-topbar-subscribe">
      <StyledSubscribeButtonDiv>
        {renderSubscribeTitle(isSubscribed)}
        <Icon name="chevronDownSmall" />
      </StyledSubscribeButtonDiv>
    </Button>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: VisitorConversationHeaderSubscribeProps,
  isSubscribed: boolean,
  onSubscribeClick: Handler,
  onUnsubscribeClick: Handler,
) {
  const {isVisitorSubscribedAll, resubscribeAllVisitorNotifications} = props;
  return (
    <SimpleDropdown {...modalProps} placement="bottom-end" width={248}>
      {isVisitorSubscribedAll
        ? renderSubscribedAllDropdown(isSubscribed, onSubscribeClick, onUnsubscribeClick)
        : renderUnsubscribedAllDropdown(resubscribeAllVisitorNotifications)}
    </SimpleDropdown>
  );
}

function renderSubscribedAllDropdown(
  isSubscribed: boolean,
  onSubscribeClick: Handler,
  onUnsubscribeClick: Handler,
) {
  return (
    <>
      <TwoLinesDropdownItem
        title={renderDropdownSubscribeTitle(isSubscribed)}
        description={renderSubscribeDescription()}
        isChecked={isSubscribed}
        onSelect={() => !isSubscribed && onSubscribeClick()}
      />
      <TwoLinesDropdownItem
        title={renderUnsubscribeTitle(isSubscribed)}
        description={renderMuteDescription()}
        isChecked={!isSubscribed}
        onSelect={() => isSubscribed && onUnsubscribeClick()}
      />
    </>
  );
}

function renderUnsubscribedAllDropdown(onResubscribeClick: Handler) {
  return (
    <StyledUnsubscribedAllDropdownDiv>
      <StyledWavingHandEnvelopeImg src={WavingHandEnvelope} alt="Waving Hand" />
      <StyledUnsubscribedAllContentDiv>
        <Trans id="visitor-resubscribe-content">
          You&apos;re unsubscribed from all current and future conversations.
        </Trans>
      </StyledUnsubscribedAllContentDiv>
      <Button size={VisualSizesEnum.SMALL} variant={ButtonVariantsEnum.PRIMARY} onClick={onResubscribeClick}>
        <StyledResubscribeButtontDiv>
          <Trans id="visitor-resubscribe-button">resubscribe</Trans>
        </StyledResubscribeButtontDiv>
      </Button>
    </StyledUnsubscribedAllDropdownDiv>
  );
}

function renderDropdownSubscribeTitle(isSubscribed: boolean) {
  if (isSubscribed) {
    return <Trans id="visitor-subscribed-title">Subscribed</Trans>;
  }
  return <Trans id="visitor-subscribe-title">Subscribe</Trans>;
}

function renderSubscribeTitle(isSubscribed: boolean) {
  if (isSubscribed) {
    return <Trans id="visitor-header-subscribed-title">Subscribed</Trans>;
  }
  return <Trans id="visitor-header-subscribe-title">Unsubscribed</Trans>;
}

function renderUnsubscribeTitle(isSubscribed: boolean) {
  if (isSubscribed) {
    return <Trans id="visitor-unsubscribe-title">Unsubscribe</Trans>;
  }
  return <Trans id="visitor-unsubscribed-title">Unsubscribed</Trans>;
}

function renderSubscribeDescription() {
  return <Trans id="visitor-subscribe-description-subscribe">Notify me of all new activities</Trans>;
}

function renderMuteDescription() {
  return <Trans id="visitor-subscribe-description-mute">Only notify me when @ mentioned</Trans>;
}
